<template>
	<AppContent class="content">
		<main class="main">
			<header>
				<h1 style="margin-bottom: 30px">15 colleges that have produced the most Super Bowl players</h1>
				<p>By Katrina Sirotta</p>

				<p style="margin-bottom: 20px; margin-right: 0">
					<router-link to="/" target="_blank" rel="noopener">JustWatch</router-link> ranked the 15 most common
					colleges of players on the rosters of Super Bowl teams throughout NFL history using data from
					<a href="https://www.pro-football-reference.com/super-bowl/" target="_blank" rel="noopener"
						>Pro-Football-Reference.com</a
					>.
				</p>

				<div class="image-container">
					<img :src="`${imgDir}/hero.jpg`" alt="Mike Ehrman // Getty Images" />
					<figcaption>Mike Ehrman // Getty Images</figcaption>
				</div>
			</header>

			<section>
				<p>
					College football success doesn't always translate to NFL success. But when a player succeeds at a
					powerhouse school, chances are they'll have a fruitful career in the pros. Of course, hundreds of
					colleges have produced football players at the highest level. In fact, 428 different schools have
					been represented on the first 56 Super Bowl rosters; however, many schools have churned out Super
					Bowl contenders that have risen to levels higher than the rest.
				</p>

				<p>
					<router-link to="/us/football" target="_blank" rel="noopener">JustWatch</router-link> ranked the 15
					most common colleges of players on the rosters of Super Bowl teams throughout NFL history using data
					from
					<a href="https://www.pro-football-reference.com/super-bowl/" target="_blank" rel="noopener"
						>Pro-Football-Reference.com</a
					>. Colleges are ranked based on their total number of appearances on rosters. The data set also
					includes the unique number of players from each college in Super Bowl history and each college's
					former players who made Super Bowl rosters most frequently. Rosters from Pro-Football-Reference.com
					are season-long and do not necessarily represent the active rosters from the day of the Super Bowl.
				</p>

				<p>
					To show the dominance of the schools on this list, 13 of the colleges had at least one player on
					either the Los Angeles Rams or Cincinnati Bengals' rosters during Super Bowl LVI, and all 15 had at
					least one player on a Super Bowl roster during the first two editions.
				</p>
			</section>

			<section>
				<div class="image-container">
					<img
						loading="lazy"
						:src="`${imgDir}/college-representation-on-super-bowl-rosters.png`"
						alt="college representation on super bowl rosters"
					/>
					<!-- <figcaption>Pro-Football-Reference.com</figcaption> -->
				</div>

				<h2>Recent Super Bowl rosters have included a wider array of schools</h2>

				<p>
					Although the 15 schools on this list were the most common alma maters seen in Super Bowl rosters,
					dozens of schools have been represented in the big game—especially in recent years. As the graph
					above shows, 2015's Super Bowl XLIX between the Seattle Seahawks and New England Patriots featured
					players from 92 different colleges, a non-strike year record. (The 1987 strike season, where the
					owners hired replacement players, had a total of 124 different colleges represented in the Super
					Bowl.)
				</p>

				<p>
					Interestingly enough, despite the 1987 season and '90s showing an increase in school representation,
					the 43 Super Bowls that took place before 2009 only broke the 80-college threshold 12 times. Eight
					Super Bowls have featured players from 80 or more different colleges in the 13 Super Bowls from 2009
					until 2021. In fact, the past four Super Bowls have broken the 80 mark.
				</p>

				<p>
					While it's clear more colleges are producing NFL-caliber talent in recent years, it comes at a time
					when college football has been dominated by a small number of schools like Alabama, Georgia,
					Clemson, and Ohio State. These numbers prove that recruiting has heightened around the country
					despite a lack of diversity regarding the National Championship.
				</p>
			</section>

			<section>
				<div class="image-container">
					<img loading="lazy" :src="`${imgDir}/15.jpg`" />
					<figcaption>Jamie Squire // Getty Images</figcaption>
				</div>

				<h2>#15. Florida State</h2>

				<ul>
					<li>Total appearances on Super Bowl rosters: 90</li>
					<li>Unique players on Super Bowl rosters: 72</li>
					<li>Most prolific players: Bryant McFadden, J.T. Thomas, Ken Lanier, Orson Mobley (3 each)</li>
				</ul>

				<p>
					Florida State University players have been
					<a
						href="https://247sports.com/college/florida-state/Article/FSU-Football-NFL-Super-Bowl-LVI-Cam-Akers-Jalen-Ramsey-Auden-Tate-182703508"
						target="_blank"
						rel="noopener"
						>represented in the big game since 1968</a
					>, when Fred Biletnikoff's Oakland Raiders lost to the Green Bay Packers. Since then, 71 other
					Seminoles have made appearances in 89 different Super Bowl rosters.
				</p>

				<p>
					Defensive back J.T. Thomas is most notable for
					<a href="https://www.pittsburghmagazine.com/j-t-thomas/" target="_blank" rel="noopener"
						>winning four Super Bowls in the '70s</a
					>
					with the Pittsburgh Steelers (<a
						href="https://steelersdepot.com/2022/06/building-an-all-time-steelers-team-non-hall-of-famers-edition-cornerbacks/"
						target="_blank"
						rel="noopener"
					>
						including one in 1978 </a
					>, which he sat out due to illness). Decades later, cornerback Bryant McFadden won two rings with
					the Pittsburgh Steelers in Super Bowl XL and XLIII. Tight end Orson Mobley appeared in three Super
					Bowls in the '80s with his team, the Denver Broncos; however, he never won the Lombardi Trophy.
				</p>
			</section>

			<section>
				<div class="image-container">
					<img loading="lazy" :src="`${imgDir}/13-01.jpg`" />
					<figcaption>Focus On Sport // Getty Images</figcaption>
				</div>

				<h2>#13. Colorado (tie)</h2>

				<ul>
					<li>Total appearances on Super Bowl rosters: 95</li>
					<li>Unique players on Super Bowl rosters: 66</li>
					<li>Most prolific players: Nate Solder, Ted Johnson (4 each)</li>
				</ul>

				<p>
					Colorado might not be the most prominent name in college football these days; however, the
					university has produced 66 players who not only made it to the NFL but appeared in the Super Bowl.
					Linebacker Ted Johnson played during Colorado's heyday in the '90s and then was drafted by the New
					England Patriots in 1995 and was part of the team that Tom Brady first led to a Super Bowl win in
					2002. He
					<a
						href="https://www.statmuse.com/nfl/ask/how-many-rings-does-ted-johnson-have"
						target="_blank"
						rel="noopener"
						>also won rings</a
					>
					with the Patriots in 2004 and 2005.
				</p>
			</section>

			<section>
				<div class="image-container">
					<img loading="lazy" :src="`${imgDir}/13-02.jpg`" />
					<figcaption>George Rose // Getty Images</figcaption>
				</div>

				<h2>#13. Alabama (tie)</h2>

				<ul>
					<li>Total appearances on Super Bowl rosters: 95</li>
					<li>Unique players on Super Bowl rosters: 63</li>
					<li>Most prolific player: Cornelius Bennett (5)</li>
				</ul>

				<p>
					Nearly
					<a
						href="https://www.si.com/college/alabama/bamanfl/alabama-players-nfl-draft-database"
						target="_blank"
						rel="noopener"
						>400 Alabama players have been drafted into the NFL</a
					>
					throughout history, so it might come as a surprise that only 63 of them have been on Super Bowl
					rosters. Of those players, Cornelius Bennett was the most prolific, with a whopping five
					appearances. The linebacker was drafted second overall in 1987 by the Indianapolis Colts, but after
					being unable to agree on a contract, he was traded to the Buffalo Bills. He made it to the big game
					four times with the Bills and once with the Atlanta Falcons in their first Super Bowl appearance
					during the 1998 season,
					<a
						href="https://www.ajc.com/sports/football/whatever-happened-cornelius-bennett/flEwdB01BgFB3yBup4rZBI/"
						target="_blank"
						rel="noopener"
					>
						but never took home the Lombardi Trophy </a
					>.
				</p>
			</section>

			<section>
				<div class="image-container">
					<img loading="lazy" :src="`${imgDir}/12.jpg`" />
					<figcaption>Jamie Squire // Getty Images</figcaption>
				</div>

				<h2>#12. Florida</h2>

				<ul>
					<li>Total appearances on Super Bowl rosters: 97</li>
					<li>Unique players on Super Bowl rosters: 75</li>
					<li>Most prolific players: Max Starks, Godfrey Myles, Emmitt Smith, Burton Lawless (3 each)</li>
				</ul>

				<p>
					Florida's football program has been elite for decades, and it shows by the number of players that
					have made it to the Super Bowl. Of the 75 who've made appearances, four have been in the big game
					three times.
				</p>

				<p>
					In the '90s, Hall of Fame running back Emmitt Smith and his Gators teammate, linebacker Godfrey
					Myles, took home the Lombardi Trophy three times with the Dallas Cowboys. Offensive tackle Max
					Starks won two rings with the Pittsburgh Steelers in the early aughts. Offensive guard Burton
					Lawless was also on three Dallas Cowboys Super Bowl teams in the '70s and won once at Super Bowl
					XII.
				</p>
			</section>

			<section>
				<div class="image-container">
					<img loading="lazy" :src="`${imgDir}/11.jpg`" />
					<figcaption>Star Tribune // Getty Images</figcaption>
				</div>

				<h2>#11. Nebraska</h2>

				<ul>
					<li>Total appearances on Super Bowl rosters: 101</li>
					<li>Unique players on Super Bowl rosters: 73</li>
					<li>Most prolific players: Russ Hochstein, Mick Tingelhoff (4 each)</li>
				</ul>

				<p>
					Nebraska players have appeared on 101 Super Bowl rosters, and interestingly enough, the two
					Cornhuskers who made it to the big game the most were both centers. Mick Tingelhoff was
					<a
						href="https://huskers.com/sports/football/roster/mick-tingelhoff/23356"
						target="_blank"
						rel="noopener"
					>
						one of the most iconic players in Nebraska history </a
					>, starting as a freshman in 1958. His story continued in the NFL, where he failed to be drafted but
					signed with the Minnesota Vikings as a free agent.
				</p>

				<p>
					Tingelhoff ended up playing 17 seasons with the Vikings—six of which he was a Pro Bowler—and was on
					all four Super Bowl teams. Though he never won a ring, the center was inducted into the Nebraska
					Football Hall of Fame in 1980 and the Pro Football Hall of Fame in 2015. Russ Hochstein's story may
					not be as heralded as Tingelhoff's; however, he won two championships with the New England Patriots,
					in 2004 and 2005, respectively.
				</p>
			</section>

			<section>
				<div class="image-container">
					<img loading="lazy" :src="`${imgDir}/09-01.jpg`" />
					<figcaption>Ezra Shaw // Getty Images</figcaption>
				</div>

				<h2>#9. Tennessee (tie)</h2>

				<ul>
					<li>Total appearances on Super Bowl rosters: 104</li>
					<li>Unique players on Super Bowl rosters: 81</li>
					<li>Most prolific player: Peyton Manning (4)</li>
				</ul>

				<p>
					Eighty-one Tennessee players have been to the Super Bowl, but one name stands out from the rest.
					After leading the Volunteers to a Southeastern Conference championship in the 1997 season, Peyton
					Manning was drafted first overall by the Indianapolis Colts. During his 13-year tenure, the
					quarterback turned around a struggling Colts team and took them to two Super Bowls, winning one.
				</p>

				<p>
					When he signed with the Denver Broncos in 2012, Manning also managed to get them to the big game
					twice and won Super Bowl 50. In 2017, he was inducted into the College Football Hall of Fame, and
					four years later, he was inducted into the Pro Football Hall of Fame.
				</p>
			</section>

			<section>
				<div class="image-container">
					<img loading="lazy" :src="`${imgDir}/09-02.jpg`" />
					<figcaption>Sylvia Allen // Getty Images</figcaption>
				</div>

				<h2>#9. Georgia (tie)</h2>

				<ul>
					<li>Total appearances on Super Bowl rosters: 104</li>
					<li>Unique players on Super Bowl rosters: 68</li>
					<li>Most prolific players: Patrick Pass, Richard Seymour (4 each)</li>
				</ul>

				<p>
					Georgia has always been one of the most elite football programs in the country, and many Bulldogs go
					on to have successful NFL careers. For two of them, that included four trips to the Super Bowl and
					winning three rings on the same team. Patrick Pass was drafted in 2000 to the New England Patriots,
					and his Georgia teammate Richard Seymour joined him the next year. Together, they won Super Bowls
					XXXVI, XXXVIII, and XXXIX.
				</p>
			</section>

			<section>
				<div class="image-container">
					<img loading="lazy" :src="`${imgDir}/08.jpg`" />
					<figcaption>Maddie Meyer // Getty Images</figcaption>
				</div>

				<h2>#8. Ohio State</h2>

				<ul>
					<li>Total appearances on Super Bowl rosters: 105</li>
					<li>Unique players on Super Bowl rosters: 74</li>
					<li>Most prolific players: Nate Ebner, Mike Vrabel, Jim Marshall (4 each)</li>
				</ul>

				<p>
					Ohio State is just one of three colleges to
					<a
						href="https://i80sportsblog.com/nfl-players-by-college-what-college-has-the-most-nfl-players/"
						target="_blank"
						rel="noopener"
						>produce more than 400 NFL players</a
					>. Of those, 74 have made it to the big game, with three former Buckeyes making four Super Bowl
					appearances each. Jim Marshall was part of all four of the Minnesota Vikings Super Bowl teams, and
					although he never won a ring, the defensive end does
					<a href="https://www.footballcolleges.com/jim-marshall/" target="_blank" rel="noopener"
						>hold numerous NFL records</a
					>.
				</p>

				<p>
					Mike Vrabel won three championships with the New England Patriots in the early aughts and pursued a
					coaching career after retiring as a player. He's been the Tennessee Titans' head coach since 2018.
					Nate Ebner also won three rings with the Patriots in 2014, 2016, and 2018.
				</p>
			</section>

			<section>
				<div class="image-container">
					<img loading="lazy" :src="`${imgDir}/07.jpg`" />
					<figcaption>Al Messerschmidt // Getty Images</figcaption>
				</div>

				<h2>#7. LSU</h2>

				<ul>
					<li>Total appearances on Super Bowl rosters: 109</li>
					<li>Unique players on Super Bowl rosters: 76</li>
					<li>Most prolific player: Kevin Faulk (5)</li>
				</ul>

				<p>
					Several former LSU players have made it to the big game, but only one has represented the school in
					the Super Bowl five times. After a storied college career, Kevin Faulk was drafted by the New
					England Patriots in 1999 and played 13 seasons with the team. Like many players on this list, the
					running back was part of the Patriots' first three Super Bowl title teams. He was inducted into the
					Patriots Hall of Fame in 2016 and the College Football Hall of Fame in 2022.
				</p>
			</section>

			<section>
				<div class="image-container">
					<img loading="lazy" :src="`${imgDir}/06.jpg`" />
					<figcaption>Bettmann // Getty Images</figcaption>
				</div>

				<h2>#6. Notre Dame</h2>

				<ul>
					<li>Total appearances on Super Bowl rosters: 110</li>
					<li>Unique players on Super Bowl rosters: 78</li>
					<li>Most prolific players: Joe Montana, Rocky Bleier, Bob Kuechenberg, Alan Page (4 each)</li>
				</ul>

				<p>
					Notre Dame is a powerhouse for shaping NFL-caliber talent,
					<a
						href="https://i80sportsblog.com/nfl-players-by-college-what-college-has-the-most-nfl-players/"
						target="_blank"
						rel="noopener"
					>
						producing a record-setting 495 NFL players </a
					>. Of those, Joe Montana is the most famous name on the list—winning four Super Bowl titles with the
					San Francisco 49ers before being inducted into the Pro Football Hall of Fame in 2000.
				</p>

				<p>
					Rocky Bleier also won four rings with the famed Steelers teams of the '70s. Before the Steelers'
					rise to dominance, Bob Kuechenberg took home two championships with the Miami Dolphins in the early
					'70s. Alan Page made it to the Super Bowl four times with the Vikings but never won the Lombardi
					Trophy.
				</p>
			</section>

			<section>
				<div class="image-container">
					<img loading="lazy" :src="`${imgDir}/05.jpg`" />
					<figcaption>Focus On Sport // Getty Images</figcaption>
				</div>

				<h2>#5. UCLA</h2>

				<ul>
					<li>Total appearances on Super Bowl rosters: 114</li>
					<li>Unique players on Super Bowl rosters: 72</li>
					<li>Most prolific player: Mike Lodish (6)</li>
				</ul>

				<p>
					Five former University of California, Los Angeles, football players have been
					<a
						href="https://www.nfl.com/photos/top-10-all-time-nfl-players-from-ucla-0ap1000000204344"
						target="_blank"
						rel="noopener"
					>
						inducted into the Pro Football Hall of Fame </a
					>—including famed quarterback Troy Aikman—but none of them have been to the Super Bowl more than
					Mike Lodish. In fact, the nose tackle participated in the big game six times, which is the
					<a
						href="https://www.sportskeeda.com/nfl/5-nfl-players-super-bowl-appearances"
						target="_blank"
						rel="noopener"
						>second-most appearances in NFL history</a
					>
					(tied with Stephen Gostkowski). His first four with the Buffalo Bills didn't end in championships;
					however, after signing with the Denver Broncos in 1995, he took home two rings after winning Super
					Bowls XXXII and XXXIII.
				</p>
			</section>

			<section>
				<div class="image-container">
					<img loading="lazy" :src="`${imgDir}/04.jpg`" />
					<figcaption>Ross Lewis // Getty Images</figcaption>
				</div>

				<h2>#4. Penn State</h2>

				<ul>
					<li>Total appearances on Super Bowl rosters: 119</li>
					<li>Unique players on Super Bowl rosters: 81</li>
					<li>Most prolific players: Matt Millen, Jack Ham, Franco Harris (4 each)</li>
				</ul>

				<p>
					The Nittany Lions have a storied history of producing elite NFL talent. Between the early '70s and
					'90s, three former Penn State players made it to the Super Bowl four times each.
				</p>

				<p>
					Pro Bowl linebacker Jack Ham and
					<a href="https://www.profootballhof.com/players/franco-harris/" target="_blank" rel="noopener"
						>Hall of Fame running back Franco Harris</a
					>
					remained teammates on the legendary Pittsburgh Steelers teams that won four championships in the
					'70s (Harris was MVP of Super Bowl IX). Matt Millen played for the Oakland and Los Angeles Raiders,
					San Francisco 49ers, and Washington Redskins, winning in each of his Super Bowl appearances and
					getting a ring with all three teams. After his football career ended, Millen made a name for himself
					as an
					<a
						href="https://www.foxsports.com/presspass/bios/on-air/matt-millen/"
						target="_blank"
						rel="noopener"
						>NFL commentator and executive</a
					>
					for the Detroit Lions.
				</p>
			</section>

			<section>
				<div class="image-container">
					<img loading="lazy" :src="`${imgDir}/03.jpg`" />
					<figcaption>Rob Carr // Getty Images</figcaption>
				</div>

				<h2>#3. Michigan</h2>

				<ul>
					<li>Total appearances on Super Bowl rosters: 122</li>
					<li>Unique players on Super Bowl rosters: 79</li>
					<li>Most prolific player: Tom Brady (10)</li>
				</ul>

				<p>
					There are plenty of players from Michigan who went on to have memorable NFL careers, but there's no
					one more prolific than Tom Brady. Considered by some to be the greatest NFL player of all time, the
					quarterback has been to the Super Bowl a
					<a
						href="https://www.sportskeeda.com/nfl/5-nfl-players-super-bowl-appearances"
						target="_blank"
						rel="noopener"
						>record-breaking 10 times</a
					>. As if that's not impressive enough, he's won seven championships (six with the New England
					Patriots and one with the Tampa Bay Buccaneers), which is more than any NFL player.
				</p>
			</section>

			<section>
				<div class="image-container">
					<img loading="lazy" :src="`${imgDir}/02.jpg`" />
					<figcaption>Al Messerschmidt // Getty Images</figcaption>
				</div>

				<h2>#2. USC</h2>

				<ul>
					<li>Total appearances on Super Bowl rosters: 123</li>
					<li>Unique players on Super Bowl rosters: 84</li>
					<li>
						Most prolific players: Willie McGinest, Ronnie Lott, Gerry Mullins, Lynn Swann, Ron Yary (4
						each)
					</li>
				</ul>

				<p>
					The University of Southern California boasts 14 players who've been
					<a
						href="https://usctrojans.com/sports/2018/8/14/usc-pro-football-hall-of-famers-nfl.aspx"
						target="_blank"
						rel="noopener"
					>
						inducted into the Pro Football Hall of Fame </a
					>, and three of those famous names appeared in four Super Bowls each. Offensive tackle Ron Yary was
					a seven-time Pro Bowler but, unfortunately, played for the Minnesota Vikings most of his career and
					was on the teams that made it to the big game four times without winning a championship. Thankfully,
					he won a National Championship with USC in 1967.
				</p>

				<p>
					Lynn Swann had better luck than Yary in the championship department. In addition to winning it all
					in 1972 with USC, the wide receiver also played for the famed Pittsburgh Steelers teams of the '70s
					that took home the Lombardi Trophy four times. Ronnie Lott also was fortunate enough to feel the
					excitement of a college championship (in 1978) and four Super Bowl wins. The legendary cornerback
					and safety was part of the '80s San Francisco 49ers that won four Super Bowls.
				</p>
			</section>

			<section>
				<div class="image-container">
					<img loading="lazy" :src="`${imgDir}/01.jpg`" />
					<figcaption>Owen C. Shaw // Getty Images</figcaption>
				</div>

				<h2>#1. Miami (Florida)</h2>

				<ul>
					<li>Total appearances on Super Bowl rosters: 124</li>
					<li>Unique players on Super Bowl rosters: 88</li>
					<li>Most prolific players: Vince Wilfork, Ted Hendricks, Jim Kelly (4 each)</li>
				</ul>

				<p>
					It's been a while since Miami was a real contender in college football, but the program has produced
					plenty of elite NFL talent, including more players on Super Bowl rosters than any other school with
					124 appearances. There have been many famous names who started as Hurricanes—including Andre
					Johnson, Warren Sapp, and Ray Lewis—but none made it to the Super Bowl more than Vince Wilfork, Ted
					Hendricks, and Jim Kelly.
				</p>

				<p>
					Hendricks played for The U before its heyday in the '80s, but the outside linebacker experienced
					plenty of championship euphoria when he won four rings (one with the Baltimore Colts and three with
					the Oakland and Los Angeles Raiders). Wilfork was part of the Canes' last championship team in 2001
					and won two Super Bowls with the New England Patriots. Kelly was inducted into the Pro Football Hall
					of Fame in 2002 but, unfortunately, never won a championship at Miami or in the NFL, despite going
					to the Super Bowl four times with the Buffalo Bills.
				</p>
			</section>
		</main>

		<AppFooter />
	</AppContent>
</template>

<script setup lang="ts">
const imgDir = `/${ASSETS_DIR}/img/sports/articles/list-15-super-bowl`;
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';
@import '~@/styles/sports.scss';

.content {
	align-items: center;

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		z-index: 0;
		width: 100%;
		height: 80vh;
		background-color: #0c1131;
	}
}

main {
	position: relative;
	z-index: 1;
	max-width: 100ch;
	text-align: justify;
	text-justify: inter-word;

	margin-inline: 64px;
	@media (min-width: 550px) {
		margin-inline: 128px;
	}

	h1,
	h2,
	p {
		margin: 0;
		padding: 0;
	}

	header {
		margin-bottom: 48px;

		h1 {
			font-size: 48px;
			font-weight: 700;
			line-height: 120%;
			color: $color-brand-default;
			text-align: left;
			margin-top: 72px;
			margin-bottom: 48px;
		}

		p {
			@include typo-sport-small;
			margin-bottom: 80px;
		}

		@media (min-width: 550px) {
			h1 {
				max-width: 30ch;
			}
		}

		@media (max-width: 550px) {
			text-align: center;
		}
	}

	section {
		margin-bottom: 60px;

		h2 {
			margin-bottom: 16px;

			font-size: 28px;
			font-weight: 900;
			color: $color-neutral-light;
		}

		p {
			@include typo-sport-small;
			margin-bottom: 16px;
		}
	}

	p {
		color: white;
	}

	a {
		color: white;
		text-decoration: underline;
	}

	ul {
		padding-left: 16px;
		list-style-type: '-   ';
		color: white;
	}

	.image-container {
		margin-bottom: 16px;

		img {
			// aspect-ratio: 656 / 400;
			width: 100%;
			// max-width: 656px;
			border-radius: 8px;
			background-color: $color-primary-light;
			object-fit: cover;
		}

		figcaption {
			margin-top: 16px;

			font-size: 14px;
			font-weight: 400;
			line-height: 16px;
			color: $color-primary-light;
		}
	}
}
</style>
